import React, { useLayoutEffect, useRef, useState } from "react";

function getScrollParent(el) {
  let node = el?.parentElement;
  while (node) {
    const { overflowY } = window.getComputedStyle(node);
    if (overflowY === "auto" || overflowY === "scroll") {
      return node;
    }
    node = node.parentElement;
  }
  return null;
}

function scrollAccordionIntoView(el) {
  if (!el) return;
  const container = getScrollParent(el);
  if (!container) {
    el.scrollIntoView({ behavior: "smooth", block: "start" });
    return;
  }
  const elRect = el.getBoundingClientRect();
  const containerRect = container.getBoundingClientRect();
  const nextTop = container.scrollTop + (elRect.top - containerRect.top);
  container.scrollTo({ top: Math.max(0, nextTop), behavior: "smooth" });
}

export function VisitColumnAccordion({
  title,
  defaultOpen = true,
  open: openProp,
  onOpenChange = null,
  headerActions = null,
  children,
  className = "",
}) {
  const [internalOpen, setInternalOpen] = useState(defaultOpen);
  const controlled = openProp !== undefined;
  const open = controlled ? !!openProp : internalOpen;
  const sectionRef = useRef(null);
  const shouldScrollRef = useRef(false);

  const setOpen = (next) => {
    if (controlled) onOpenChange?.(next);
    else setInternalOpen(next);
  };

  useLayoutEffect(() => {
    if (!open || !shouldScrollRef.current) return;
    shouldScrollRef.current = false;
    const el = sectionRef.current;
    const scrollOpened = () => scrollAccordionIntoView(el);
    scrollOpened();
    const timer = window.setTimeout(scrollOpened, 80);
    return () => window.clearTimeout(timer);
  }, [open]);

  return (
    <section
      ref={sectionRef}
      className={`gvisit-col-accordion${open ? " is-open" : ""} ${className}`.trim()}
    >
      <div className="gvisit-col-accordion__head">
        <button
          type="button"
          className="gvisit-col-accordion__toggle"
          aria-expanded={open}
          onClick={() => {
            const next = !open;
            shouldScrollRef.current = next;
            setOpen(next);
          }}
        >
          <i
            className={`fas ${open ? "fa-minus-circle" : "fa-plus-circle"}`}
            aria-hidden
          />
          <span>{title}</span>
        </button>
        {headerActions ? (
          <div className="gvisit-col-accordion__actions">{headerActions}</div>
        ) : null}
      </div>
      <div
        className="gvisit-col-accordion__body"
        hidden={!open}
      >
        {children}
      </div>
    </section>
  );
}

export function FeatureOffNote({ title, impact, className = "" }) {
  return (
    <section className={`gvisit-setup-card gvisit-setup-card--off ${className}`.trim()}>
      <div className="gvisit-setup-card__head">
        {title ? <h4 className="gvisit-setup-card__title">{title}</h4> : null}
        <span className="gvisit-feature-off-badge">Off</span>
      </div>
      {impact ? <p className="gvisit-feature-off-impact">{impact}</p> : null}
    </section>
  );
}

export function StaticSetupSection({
  title,
  headerExtra = null,
  onSave,
  saving = false,
  saveLabel = "Save",
  readOnly = false,
  children,
  className = "",
}) {
  return (
    <section className={`gvisit-setup-card ${className}`.trim()}>
      <div className="gvisit-setup-card__head">
        <h4 className="gvisit-setup-card__title">{title}</h4>
        {headerExtra}
      </div>
      <div className="gvisit-setup-card__body">
        {children}
        {onSave && !readOnly ? (
          <div className="gvisit-section-foot">
            <button
              type="button"
              className="btn btn-outline-primary btn-sm"
              disabled={saving}
              onClick={onSave}
            >
              {saving ? "Saving…" : saveLabel}
            </button>
          </div>
        ) : null}
      </div>
    </section>
  );
}

export function CollapsibleSection({
  title,
  defaultOpen = true,
  onSave,
  saving = false,
  saveLabel = "Save",
  readOnly = false,
  children,
  className = "",
}) {
  const [open, setOpen] = useState(defaultOpen);

  return (
    <section className={`gvisit-fold gvisit-fold--dashed${open ? " is-open" : ""} ${className}`.trim()}>
      <div className="gvisit-fold__head">
        <button
          type="button"
          className="gvisit-fold__toggle"
          aria-expanded={open}
          onClick={() => setOpen((v) => !v)}
        >
          <i
            className={`fas ${open ? "fa-minus-circle" : "fa-plus-circle"}`}
            aria-hidden
          />
          <span>{title}</span>
        </button>
      </div>
      {open ? (
        <div className="gvisit-fold__body">
          {children}
          {onSave && !readOnly ? (
            <div className="gvisit-section-foot">
              <button
                type="button"
                className="btn btn-outline-primary btn-sm"
                disabled={saving}
                onClick={onSave}
              >
                {saving ? "Saving…" : saveLabel}
              </button>
            </div>
          ) : null}
        </div>
      ) : null}
    </section>
  );
}

export function MiniField({ label, required, error, children }) {
  return (
    <div className="gvisit-mini-field">
      <span className="gvisit-mini-field__label">
        {label}
        {required ? <span className="gvisit-legacy-field__req">*</span> : null}
      </span>
      {children}
      {error ? <span className="gvisit-mini-field__error">{error}</span> : null}
    </div>
  );
}

/** Legacy CTMS style: "Screening (Euro €)" — single parentheses only */
export function formatCurrencyFieldLabel(baseName, currencySymbol) {
  const raw = String(currencySymbol || "£").trim();
  const symMatch = raw.match(/[€£$]/);
  const sym = symMatch?.[0] || raw.replace(/[()]/g, "").trim() || "£";
  const plain = raw.replace(/[()]/g, "").trim().toLowerCase();

  let name = "";
  if (plain.includes("euro") || sym === "€") {
    name = "Euro";
  } else if (plain.includes("pound") || sym === "£") {
    name = "Pound sterling";
  } else if (plain.includes("dollar") || sym === "$") {
    name = "US Dollar";
  }

  return name ? `${baseName} (${name} ${sym})` : `${baseName} (${sym})`;
}

export function NumberBox({
  value,
  onChange,
  maxLength = 4,
  allowNegative = false,
  allowDecimal = false,
  maxDecimalPlaces = 2,
  readOnly = false,
  disabled = false,
  placeholder = "",
  onBlur,
  invalid = false,
}) {
  const handleChange = (raw) => {
    if (readOnly || disabled) return;
    let next = String(raw ?? "");
    if (allowDecimal) {
      next = next.replace(/[^0-9.]/g, "");
      const firstDot = next.indexOf(".");
      if (firstDot === -1) {
        // integer-only path while typing
      } else {
        const intPart = next.slice(0, firstDot);
        const decRaw = next.slice(firstDot + 1).replace(/\./g, "");
        const decPart = decRaw.slice(
          0,
          Math.max(0, Number(maxDecimalPlaces) || 0),
        );
        if (next.endsWith(".") && decPart === "") {
          next = `${intPart}.`;
        } else {
          next = `${intPart}.${decPart}`;
        }
      }
    } else if (allowNegative) {
      next = next.replace(/[^0-9-]/g, "");
      if (next.includes("-")) {
        next = `-${next.replace(/-/g, "")}`;
      }
    } else {
      next = next.replace(/[^0-9]/g, "");
    }
    onChange?.(next);
  };

  return (
    <input
      type="text"
      inputMode={allowDecimal ? "decimal" : allowNegative ? "text" : "numeric"}
      className={`gvisit-num-box${invalid ? " is-invalid" : ""}`}
      value={value ?? ""}
      maxLength={maxLength}
      placeholder={placeholder}
      readOnly={readOnly}
      disabled={disabled}
      onChange={(e) => handleChange(e.target.value)}
      onBlur={(e) => {
        if (
          allowDecimal &&
          !readOnly &&
          !disabled &&
          String(value ?? "").endsWith(".")
        ) {
          onChange?.(String(value).slice(0, -1));
        }
        onBlur?.(e);
      }}
    />
  );
}

/** Min / max window fields — fixed − or + prefix, digits only (legacy CTMS style). */
export function SignedNumberBox({
  sign = "-",
  value,
  onChange,
  maxLength = 3,
  disabled = false,
}) {
  const digits = String(value ?? "").replace(/[^0-9]/g, "");

  return (
    <div className="gvisit-num-box-wrap">
      <span className="gvisit-num-box-wrap__sign" aria-hidden>
        {sign}
      </span>
      <input
        type="text"
        inputMode="numeric"
        className="gvisit-num-box gvisit-num-box--signed"
        value={digits}
        maxLength={maxLength}
        disabled={disabled}
        onChange={(e) => onChange?.(e.target.value.replace(/[^0-9]/g, ""))}
      />
    </div>
  );
}
